import dayjs from "dayjs";

// Stable enum keys (ECL convention): never store or compare display strings.
export const BADGE_STATUS = Object.freeze({
  GOOD_STANDING: "good_standing",
  OBSOLETE: "obsolete",
  EXPIRED: "expired",
  UPGRADED: "upgraded",
  REVOKED: "revoked",
});

export const BADGE_STATUS_UNKNOWN = "unknown";

export const ISSUING_METHOD = Object.freeze({
  GIVEN: "given",
  AWARDED: "awarded",
});

export const BADGE_STATUS_LABELS = Object.freeze({
  [BADGE_STATUS.GOOD_STANDING]: "Good Standing",
  [BADGE_STATUS.OBSOLETE]: "Obsolete",
  [BADGE_STATUS.EXPIRED]: "Expired",
  [BADGE_STATUS.UPGRADED]: "Upgraded",
  [BADGE_STATUS.REVOKED]: "Revoked",
  [BADGE_STATUS_UNKNOWN]: "Unknown status",
});

export const ISSUING_METHOD_LABELS = Object.freeze({
  [ISSUING_METHOD.GIVEN]: "Given",
  [ISSUING_METHOD.AWARDED]: "Awarded",
});

/** A badge is current (and counts toward readiness) only in these statuses. */
export const READINESS_BADGE_STATUSES = Object.freeze([
  BADGE_STATUS.GOOD_STANDING,
  BADGE_STATUS.OBSOLETE,
]);

export const TERMINAL_BADGE_STATUSES = Object.freeze([
  BADGE_STATUS.EXPIRED,
  BADGE_STATUS.UPGRADED,
  BADGE_STATUS.REVOKED,
]);

const KNOWN_STATUSES = Object.values(BADGE_STATUS);
const KNOWN_METHODS = Object.values(ISSUING_METHOD);

function toKey(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/**
 * Stable status key for a payload value (`lifecycle_status` arrives as GOOD_STANDING).
 * Returns null when no status was supplied and `unknown` when it is not recognised.
 */
export function resolveBadgeStatus(value) {
  const key = toKey(value);
  if (!key) return null;
  return KNOWN_STATUSES.includes(key) ? key : BADGE_STATUS_UNKNOWN;
}

/** Stable issuing-method key, or null when absent or not recognised. */
export function resolveIssuingMethod(value) {
  const key = toKey(value);
  return KNOWN_METHODS.includes(key) ? key : null;
}

export function getBadgeStatusLabel(status) {
  return BADGE_STATUS_LABELS[status] || BADGE_STATUS_LABELS[BADGE_STATUS_UNKNOWN];
}

export function getIssuingMethodLabel(method) {
  return ISSUING_METHOD_LABELS[method] || "";
}

export function isTerminalBadgeStatus(status) {
  return TERMINAL_BADGE_STATUSES.includes(status);
}

/** Readiness counts Good Standing and Obsolete badges only. */
export function countsTowardReadiness(status) {
  return READINESS_BADGE_STATUSES.includes(status);
}

/** Flag gate: with idea-MDP-632 off (and no override) badges render exactly as before. */
export function wantsBadgeStatusModel({ badgeStatusWithoutFlag, ideaMdp632 } = {}) {
  return badgeStatusWithoutFlag === true || ideaMdp632 === true;
}

/** Display name of whoever ended the badge; a bare id carries no name. */
export function formatBadgeEndedBy(endedBy) {
  if (!endedBy || typeof endedBy === "number") return "";
  if (typeof endedBy === "string") return endedBy.trim();
  const fullName = [endedBy.first_name, endedBy.last_name].filter(Boolean).join(" ");
  return String(endedBy.name || endedBy.full_name || fullName || "").trim();
}

function formatEndedOn(endedOn) {
  if (!endedOn) return "";
  const parsed = dayjs(endedOn);
  return parsed.isValid() ? parsed.format("DD-MMMM-YYYY") : "";
}

/**
 * Tooltip copy for a badge: `title` carries level, status and issuing method;
 * `description` carries ended_on / ended_by / end_reason for terminal states.
 */
export function buildBadgeStatusTooltip({
  levelLabel = "",
  status,
  issuingMethod = null,
  endedOn = null,
  endedBy = null,
  endReason = "",
} = {}) {
  const statusLabel = getBadgeStatusLabel(status);
  const methodLabel = getIssuingMethodLabel(issuingMethod);
  const title = [[levelLabel, statusLabel].filter(Boolean).join(" "), methodLabel]
    .filter(Boolean)
    .join(" · ");

  if (!isTerminalBadgeStatus(status)) {
    return { title, description: "" };
  }

  const date = formatEndedOn(endedOn);
  const by = formatBadgeEndedBy(endedBy);
  const reason = String(endReason ?? "").trim();
  const description = [
    date && `${statusLabel} on ${date}`,
    by && `${statusLabel} by ${by}`,
    reason && `Reason: ${reason}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return { title, description };
}

/** ReadinessBadge `dot` for a status; "No Dot" and terminal statuses keep the caller's dot. */
export function getDotForBadgeStatus(status, dot) {
  if (dot === "No Dot") return dot;
  if (status === BADGE_STATUS.OBSOLETE) return "Obsolete";
  if (status === BADGE_STATUS_UNKNOWN) return "Unknown";
  if (status === BADGE_STATUS.GOOD_STANDING) {
    return ["Good Standing", "Given", "Appointed"].includes(dot) ? dot : "Good Standing";
  }
  return dot;
}
